import { onMounted } from 'vue'
import { Preferences } from '@capacitor/preferences'
import { Capacitor } from '@capacitor/core'
import { usePostHog } from '@/composables/usePostHog'
import {
  ensureCampaignTrackingBootstrap,
  getCampaignProperties,
} from '@/composables/useCampaignTracking'
import { AnalyticsEvent } from '@/constants/analyticsEvents'

const PREF_KEY = 'balance_analytics_app_first_open_v1'

function platformLabel(): string {
  if (!Capacitor.isNativePlatform()) return 'web'
  const p = Capacitor.getPlatform()
  if (p === 'ios' || p === 'android') return p
  return 'mobile'
}

/**
 * Fires {@link AnalyticsEvent.APP_FIRST_OPENED} once per install (Preferences gate).
 * Merges in any stored UTM params and deep link referrer from the marketing funnel.
 */
async function recordAppFirstOpenedOnce(): Promise<void> {
  try {
    const { value } = await Preferences.get({ key: PREF_KEY })
    if (value === '1') return

    await ensureCampaignTrackingBootstrap()

    const { track } = usePostHog()
    const campaign = await getCampaignProperties()

    track(AnalyticsEvent.APP_FIRST_OPENED, {
      platform: platformLabel(),
      app_version: import.meta.env.VITE_APP_VERSION,
      utm_source: campaign.utm_source,
      utm_medium: campaign.utm_medium,
      utm_campaign: campaign.utm_campaign,
      utm_content: campaign.utm_content,
      utm_term: campaign.utm_term,
      referrer_url: campaign.referrer_url,
      campaign_id: campaign.campaign_id,
    })

    await Preferences.set({ key: PREF_KEY, value: '1' })
  } catch (error) {
    console.warn('recordAppFirstOpenedOnce failed:', error)
  }
}

/**
 * Registers a one-shot acquisition event on app mount. Non-blocking so other
 * onMounted work (theme, interceptors) is not delayed by Preferences I/O.
 */
export function useRecordAppFirstOpened() {
  onMounted(() => {
    void recordAppFirstOpenedOnce()
  })
}
